import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import type { ActivityType } from '../constants';

type TypeFilter = ActivityType | 'all';

/**
 * Persisted view-mode preference for the Activities list (CLAUDE.md §2.3).
 *
 * Search and type filter live as local page state — those are server-driven
 * query params, not durable user prefs. Only the view representation persists.
 */
export type ActivitiesViewMode = 'table' | 'list' | 'cards' | 'grouped';

export const ACTIVITIES_VIEW_MODES: ActivitiesViewMode[] = [
  'table',
  'list',
  'cards',
  'grouped',
];

interface ActivitiesUiState {
  view: ActivitiesViewMode;
  setView: (view: ActivitiesViewMode) => void;
  /** Transient toggle — refreshes default to the active list. */
  showArchived: boolean;
  setShowArchived: (showArchived: boolean) => void;
  /** Current DRF `OrderingFilter` value (e.g. `-occurred_at`). Session-only. */
  ordering: string | undefined;
  setOrdering: (ordering: string | undefined) => void;
  /** Server-side "owner = me" filter (Activity has `owner`). Session-only. */
  mine: boolean;
  setMine: (mine: boolean) => void;
  /** Persisted filters — survive reloads. */
  type: TypeFilter;
  setType: (type: TypeFilter) => void;
  /** Owner (salesperson) filter — a User id as a string; '' = all. */
  owner: string;
  setOwner: (owner: string) => void;
  occurredFrom: string;
  setOccurredFrom: (v: string) => void;
  occurredTo: string;
  setOccurredTo: (v: string) => void;
}

export const useActivitiesUi = create<ActivitiesUiState>()(
  persist(
    (set) => ({
      view: 'table',
      setView: (view) => set({ view }),
      showArchived: false,
      setShowArchived: (showArchived) => set({ showArchived }),
      ordering: undefined,
      setOrdering: (ordering) => set({ ordering }),
      mine: false,
      setMine: (mine) => set({ mine }),
      type: 'all',
      setType: (type) => set({ type }),
      owner: '',
      setOwner: (owner) => set({ owner }),
      occurredFrom: '',
      setOccurredFrom: (occurredFrom) => set({ occurredFrom }),
      occurredTo: '',
      setOccurredTo: (occurredTo) => set({ occurredTo }),
    }),
    {
      name: 'crm.activities:ui',
      partialize: (s) => ({
        view: s.view,
        type: s.type,
        owner: s.owner,
        occurredFrom: s.occurredFrom,
        occurredTo: s.occurredTo,
      }),
    },
  ),
);
